import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { CustomerReportModel, GetCustomerSettingsResponse, ReportType } from '../../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationDispatch, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { addCustomerReport, removeCustomerReport } from '../../../../../Redux/Slices/CommodityManagementSlice';
import { GroupingOption } from '../../../../../Types';
import { StoneXTab, StoneXTabs } from '../../../../StoneX';
import AddReportModal from './AddReportModal';
import ReportSettingsEditor from './ReportSettingsEditor';


export default function ReportSettings() {
  const dispatch = useApplicationDispatch();
  const customerReports = useApplicationSelector((state: RootState): CustomerReportModel[] => state.commodityManagement.customerReports ?? []);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [isAddReportModalOpen, setIsAddReportModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (customerReports.length === 0) {
      setSelectedReportId(null);
      return;
    }

    const selectedExists = customerReports.some((report) => report.customerReportId === selectedReportId);

    if (!selectedExists) {
      setSelectedReportId(customerReports[0].customerReportId);
    }
  }, [customerReports, selectedReportId]);

  const selectedReport = useMemo(() => {
    if (selectedReportId === null) {
      return null;
    }

    return customerReports.find((report) => report.customerReportId === selectedReportId) ?? null;
  }, [customerReports, selectedReportId]);

  const groupingOptions = useMemo(() => {
    const options: GroupingOption[] = ['Commodity', 'BudgetMonth'];

    if (customerSettings?.positionsByLocation) {
      options.push('BudgetingTier');
      options.push('Tiers');
    }

    if (customerSettings?.positionsByCounterparty) {
      options.push('Counterparty');
    }

    return options;
  }, [customerSettings?.positionsByCounterparty, customerSettings?.positionsByLocation]);

  const reportTypeOptions = useMemo(() => {
    return Object.values(ReportType).filter((reportType) => reportType !== ReportType.Position);
  }, []);

  const reportTypeLabelMap = useMemo<Record<ReportType, string>>(() => ({
    [ReportType.MarkToMarket]: 'Mark To Market',
    [ReportType.VarianceToBudget]: 'Variance To Budget',
    [ReportType.Scenario]: 'Scenario',
    [ReportType.TimeTrigger]: 'Time Trigger',
    [ReportType.ProfitAndLoss]: 'Profit And Loss',
    [ReportType.Summary]: 'Summary',
    [ReportType.Position]: 'Position',
    [ReportType.Exposure]: 'Exposure',
    [ReportType.DailyPositions]: 'Daily Positions',
    [ReportType.HedgePlan]: 'Hedge Plan',
    [ReportType.MarkToMarketClosed]: 'MTM Closed Positions',
    [ReportType.GrossHedging]: 'Gross Hedging',
    [ReportType.WeightedCostAverage]: 'Weighted Cost Average',
    [ReportType.Combined]: 'Combined',
    [ReportType.OpenContracts]: 'Open Contracts',
    [ReportType.HedgingPosition]: 'Hedging Position',
    [ReportType.HedgingMtm]: 'Hedging MTM',
    [ReportType.PiedmontHedging]: 'Piedmont Hedging Batch',
  }), []);

  function onReportTabChange(reportId: number) {
    setSelectedReportId(reportId);
  }

  function openAddReportModal() {
    setIsAddReportModalOpen(true);
  }

  function closeAddReportModal() {
    setIsAddReportModalOpen(false);
  }

  function onDeleteReport(customerReportId: number) {
    CommodityManagementApi.deleteCustomerReport(null, customerReportId.toString())
      .then(() => {
        dispatch(removeCustomerReport({ customerReportId }));
      })
      .catch(() => undefined);
  }

  function onReportsAdded(addedReports: CustomerReportModel[]) {
    addedReports.forEach((report) => dispatch(addCustomerReport({ report })));
  }

  return (
    <div style={{ display: 'flex', flexGrow: 1}}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StoneXTabs orientation="vertical">
            {[
              ...customerReports.filter(x => x.reportType != 'Position').map((report) => (
                <StoneXTab
                  key={report.customerReportId}
                  tabIndex={report.customerReportId}
                  isActive={report.customerReportId === selectedReportId}
                  onClick={onReportTabChange}
                  label={report.reportName}
                />
              )),
              // The dashboard's Futures Allocation report has no report type of its own; its
              // settings live in the site's Position report, which is otherwise not editable here.
              ...customerReports.filter(x => x.reportType === 'Position').map((report) => (
                <StoneXTab
                  key={report.customerReportId}
                  tabIndex={report.customerReportId}
                  isActive={report.customerReportId === selectedReportId}
                  onClick={onReportTabChange}
                  label="Futures Allocation"
                />
              )),
              <StoneXTab
                key={-1}
                tabIndex={-1}
                onClick={openAddReportModal}
                label="+ Add new report"
              />
            ]}
          </StoneXTabs>
      </div>

      <div style={{ flexGrow: 1, padding: '16px' }}>
        {selectedReport && (
          <ReportSettingsEditor report={selectedReport} groupingOptions={groupingOptions} onDeleteReport={onDeleteReport} />
        )}

        {!selectedReport && <div>No reports found.</div>}
      </div>
      <AddReportModal
        open={isAddReportModalOpen}
        onClose={closeAddReportModal}
        customerReports={customerReports}
        reportTypeOptions={reportTypeOptions}
        reportTypeLabelMap={reportTypeLabelMap}
        onReportsAdded={onReportsAdded}
        onReportSelected={onReportTabChange}
      />
    </div>
  );
}
